import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Download } from 'lucide-react'
import { PageHeader, PageShell } from '@/components/portal/page-header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { InvoiceReadingPanel } from '@/components/billing/invoice-reading-panel'
import { PayNowFlow } from '@/components/member/pay-now-flow'
import { useInvoice } from '@/hooks/use-billing'
import { useInitiateStkPush, usePayNowInfo } from '@/hooks/use-payments'
import { getApiErrorMessage } from '@/lib/api'
import { downloadInvoicePdf } from '@/lib/billing'
import { formatKES } from '@/lib/utils'
import type { Invoice, InvoiceStatus } from '@riverpark/types'

function StatusBadge({ status }: { status: InvoiceStatus }) {
  switch (status) {
    case 'paid':
      return <Badge variant="paid">Paid</Badge>
    case 'overdue':
      return <Badge variant="overdue">Overdue</Badge>
    case 'sent':
      return <Badge variant="pending">Sent</Badge>
    case 'voided':
      return <Badge variant="default">Voided</Badge>
    default:
      return <Badge variant="draft">Draft</Badge>
  }
}

/** The draft→sent→paid/overdue lifecycle is meaningless on a credit note — show whether it still
 * has balance left to use instead. */
function InvoiceStatusDisplay({ invoice }: { invoice: Invoice }) {
  if (invoice.invoiceType !== 'credit_note') return <StatusBadge status={invoice.status} />
  if (invoice.status === 'voided') return <Badge variant="default">Voided</Badge>
  const remaining = Math.round((Math.abs(invoice.amount) - invoice.amountPaid) * 100) / 100
  return remaining > 0 ? (
    <Badge variant="credit">Available</Badge>
  ) : (
    <Badge variant="default">Used</Badge>
  )
}

function formatDate(unixSeconds?: number) {
  if (!unixSeconds) return '—'
  return new Date(unixSeconds * 1000).toLocaleDateString('en-KE', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between border-b border-border-2 py-2.5 last:border-0">
      <span className="font-sans text-sm text-muted-foreground">{label}</span>
      <span className="font-sans text-sm font-medium text-ink">{value}</span>
    </div>
  )
}

export function MemberInvoiceDetail() {
  const { id } = useParams<{ id: string }>()
  const { data, isLoading, error } = useInvoice(id ?? null)
  const invoice = data?.data
  const { data: payInfoData } = usePayNowInfo()
  const payInfo = payInfoData?.data
  const initiateThisInvoice = useInitiateStkPush()
  const [downloadingPdf, setDownloadingPdf] = useState(false)
  const [pdfError, setPdfError] = useState<string | null>(null)

  const backLink = (
    <Link
      to="/bills"
      className="inline-flex items-center gap-2 font-sans text-sm text-primary hover:text-green-lt"
    >
      <ArrowLeft className="size-4" aria-hidden />
      Back to bills
    </Link>
  )

  if (isLoading) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-muted-foreground">Loading invoice…</p>
      </PageShell>
    )
  }

  if (error || !invoice) {
    return (
      <PageShell>
        <p className="font-sans text-sm text-destructive">Invoice not found.</p>
        <div className="mt-4">{backLink}</div>
      </PageShell>
    )
  }

  const canPayThisInvoice =
    invoice.invoiceType !== 'credit_note' &&
    invoice.status !== 'paid' &&
    invoice.status !== 'voided' &&
    invoice.balance > 0

  async function handleDownloadPdf() {
    if (!invoice) return
    setPdfError(null)
    setDownloadingPdf(true)
    try {
      await downloadInvoicePdf(invoice)
    } catch (err) {
      setPdfError(getApiErrorMessage(err, 'Failed to download invoice PDF'))
    } finally {
      setDownloadingPdf(false)
    }
  }

  return (
    <PageShell>
      <PageHeader
        label="Finance"
        title={invoice.invoiceNumber}
        description={invoice.utilityTypeName ?? undefined}
        action={backLink}
      />

      <div className="max-w-xl space-y-4">
        <div className="rounded border border-border-2 bg-card px-4">
          <Row label="Status" value={<InvoiceStatusDisplay invoice={invoice} />} />
          <Row label="Plot" value={invoice.plotNumber} />
          <Row label="Billing period" value={invoice.billingPeriod ?? '—'} />
          <Row label="Description" value={invoice.description} />
          <Row
            label={invoice.invoiceType === 'credit_note' ? 'Original amount' : 'Amount'}
            value={formatKES(Math.abs(invoice.amount))}
          />
          {invoice.invoiceType === 'standard' && invoice.amountPaid > 0 && (
            <>
              <Row label="Paid so far" value={formatKES(invoice.amountPaid)} />
              <Row label="Balance" value={formatKES(invoice.balance)} />
            </>
          )}
          {invoice.invoiceType === 'credit_note' && invoice.status !== 'voided' && (
            <Row
              label="Remaining"
              value={formatKES(
                Math.max(
                  0,
                  Math.round((Math.abs(invoice.amount) - invoice.amountPaid) * 100) / 100,
                ),
              )}
            />
          )}
          <Row label="Due date" value={formatDate(invoice.dueDate)} />
          <Row label="Created" value={formatDate(invoice.createdAt)} />
        </div>

        <InvoiceReadingPanel invoiceId={invoice.id} />

        <div>
          <Button
            variant="secondary"
            loading={downloadingPdf}
            onClick={() => void handleDownloadPdf()}
          >
            <Download size={14} className="mr-1.5" />
            Download PDF
          </Button>
          {pdfError && (
            <p className="mt-2 font-sans text-xs text-destructive" role="alert">
              {pdfError}
            </p>
          )}
        </div>

        {canPayThisInvoice && payInfo && (
          <PayNowFlow
            amount={invoice.balance}
            defaultPhone={payInfo.phone}
            paybill={payInfo.paybill}
            accountReference={invoice.invoiceNumber}
            isPaying={initiateThisInvoice.isPending}
            onPay={async (phone) => {
              const { data: result } = await initiateThisInvoice.mutateAsync({
                phone,
                target: { invoiceId: invoice.id },
              })
              return result
            }}
          />
        )}
      </div>
    </PageShell>
  )
}
